import { createContext, useContext, useEffect, useState } from "react";
import api, { setAuthToken } from "@/lib/api";
import { logError } from "@/lib/logger";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore the session from the httpOnly cookie; slide it on each app load.
  const loadUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      api.post("/auth/refresh").catch(() => {});
    } catch (e) {
      setAuthToken(null);
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // If returning from Google OAuth (session_id in URL hash), let AuthCallback handle it.
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    loadUser();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setAuthToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const loginWithGoogle = async (sessionId) => {
    const { data } = await api.post("/auth/google/session", { session_id: sessionId });
    setAuthToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setAuthToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { logError("auth:logout", e); /* clear locally regardless */ }
    setAuthToken(null);
    setUser(false);
  };

  const updateProfile = async (payload) => {
    const { data } = await api.put("/auth/me", payload);
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithGoogle, register, logout, updateProfile, refresh: loadUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
